import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import {
  BehaviorSubject,
  Subject,
  Observable,
  takeUntil,
  finalize,
  tap,
} from 'rxjs';
import {
  ErrorDetail,
  ErrorResponse,
} from '../../../../../core/models/error.models';
import { OrgManagementMode } from '../../../manage-organization.enums';
import { ManageOrganizationDrawerParams } from '../../../manage-organization.interface';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { InsightsService } from '../../insights.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { CommonModule } from '@angular/common';
import { UploadProgressComponent } from '../../../../../components/upload-progress/upload-progress.component';
import { UploadEvent } from '../../../../../core/services/upload-progress.service';

@Component({
  selector: 'app-import-student',
  standalone: true,
  imports: [
    NzFormModule,
    NzUploadModule,
    SvgIconComponent,
    NzButtonModule,
    NzSpinModule,
    CommonModule,
    ReactiveFormsModule,
    UploadProgressComponent,
  ],
  templateUrl: './import-student.component.html',
  styleUrl: './import-student.component.scss',
})
export class ImportStudentComponent implements OnDestroy {
  userForm!: FormGroup;
  fileList: NzUploadFile[] = [];
  uploading = false;
  private errorsSubject = new BehaviorSubject<ErrorDetail[]>([]);
  errors$ = this.errorsSubject.asObservable();
  private readonly destroy$ = new Subject<void>();
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.CREATE,
  };
  private progressSubject = new BehaviorSubject<number>(0);
  uploadProgress$ = this.progressSubject.asObservable();

  loading$: Observable<boolean>;

  constructor(
    private fb: FormBuilder,
    private message: NzMessageService,
    private readonly drawerService: DrawerService,
    private commonService: CommonService,
    private loaderService: LoaderService,
    private insightService: InsightsService,
    private cdr: ChangeDetectorRef
  ) {
    this.userForm = this.fb.group({
      roleIds: [null],
      file: [null, [Validators.required]],
    });
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    const isValidType =
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'text/xlsx' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    if (!isValidType) {
      this.message.error('You can only upload XLS, XLSX files.');
      return false;
    }

    this.fileList = [file];
    return false;
  };

  closeImportDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.STUDENT);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleFileRemove = (file: NzUploadFile): boolean => {
    this.fileList = this.fileList.filter(item => item !== file);
    return true;
  };

  submitForm = (): void => {
    const organizationId = this.orgParams.organizationId;

    if (this.fileList.length === 0) {
      this.message.error('Please select students file');
      return;
    }

    this.loaderService.show();

    this.insightService
      .bulkCreateUsers(this.fileList[0] as unknown as File, organizationId)
      .pipe(
        tap((event: UploadEvent<void>) => {
          if (event.status === 'progress') {
            this.progressSubject.next(event.percentage);
          } else if (event.status === 'completed') {
            this.progressSubject.next(100);
          }
        }),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: (event: UploadEvent<void>) => {
          if (event.status === 'completed') {
            this.commonService.notify(
              'success',
              `Students import successfully`
            );
            this.userForm.reset();
            this.closeImportDrawer();
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          if (message === 'Request Timeout') {
            this.progressSubject.next(80);
          } else {
            this.progressSubject.next(0);
          }

          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify(
            'error',
            errorMessages.length
              ? errorMessages
              : 'Please check your file format and try again',
            message
          );
        },
      });
  };
}
